import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuTrigger, MatMenu } from "@angular/material/menu";
import { Router } from "@angular/router";
import { SolverService } from "@app/services/rest/solver.service";
import { SolverInfo, SolverIssue } from "@app/models/dto/solver-issue";
import { ResourceAvailability } from "@app/models/rest/resource-availability";
import { Ticket_DetailProjection } from "@app/models/rest/ticket";
import { ResourceAvailabilityStore } from "@app/services/store/resource-availability-store";
import moment from "moment";
import { TicketStreamingStore } from "@app/services/streaming/ticket-streaming-store";
import { TicketActionDialogComponent } from "@app/view/planning/ticket-action-dialog/ticket-action-dialog.component";
import { MeetingActionDialogComponent } from "@app/view/planning/meeting-action-dialog/meeting-action-dialog.component";
import { AvailabilityEditorComponent } from "@app/view/diary/availability-editor/availability-editor.component";
import { TicketIssue } from "@app/view/shared/alerts/alert-tool/ticket.issue";
import { DayProblems } from "@app/view/shared/alerts/alert-tool/day.problems";
import { AlertData } from "@app/view/shared/alerts/alert-tool/alert.data";
import { MatIconButton } from "@angular/material/button";
import { NgClass } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MomentModule } from "ngx-moment";
import { translateSolverRule } from "@app/core/utils/solver-rule-translations";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { UserService } from "@app/services/rest/user.service";
import { ScheduleProjectService } from "@app/services/project/schedule-project.service";

@Component({
  selector: "app-alert-tool",
  templateUrl: "./alert-tool.component.html",
  styleUrls: ["./alert-tool.component.scss"],
  imports: [
    MatIconButton,
    MatMenuTrigger,
    NgClass,
    MatIcon,
    MatMenu,
    MomentModule
  ]
})
export class AlertToolComponent implements OnInit {
  solver = inject(SolverService);
  dialog = inject(MatDialog);
  router = inject(Router);
  resourceAvailabilityStore = inject(ResourceAvailabilityStore);
  ticketStore = inject(TicketStreamingStore);
  clientConfigService = inject(ClientConfigService);
  userService = inject(UserService);
  scheduleProjectService = inject(ScheduleProjectService);

  @ViewChild("alertTrigger", { read: MatMenuTrigger, static: false })
  public alertTriggerBtn: MatMenuTrigger;
  public hasAlerts = false;
  alertData: AlertData;
  alertCount: number = 0;
  resourceAvailabilities: Map<number, ResourceAvailability> = new Map<
    number,
    ResourceAvailability
  >();
  tickets: Map<number, Ticket_DetailProjection> = new Map<
    number,
    Ticket_DetailProjection
  >();
  private lastAlertData: SolverInfo;
  private plannerTeamNames: Set<string> = new Set();

  private isTicketInPlannerTeams(ticketId: number): boolean {
    const project = this.scheduleProjectService.projectData.getValue();
    const event = project.eventStore.getById(ticketId);
    if (!event) return false;
    const resourceId = (event as any).get("resourceId");
    if (!resourceId) return false;
    const resource = project.resourceStore.getById(resourceId);
    if (!resource) return false;
    const teamName = (resource as any).get("team");
    return teamName && this.plannerTeamNames.has(teamName);
  }

  private filterTicketsByTeam(ticketIds: number[]): number[] {
    if (
      !this.clientConfigService.config.restrictAlertsByPlannerTeams ||
      this.plannerTeamNames.size === 0
    ) {
      return ticketIds;
    }
    return ticketIds.filter((id) => this.isTicketInPlannerTeams(id));
  }

  updateAlerts(value: SolverInfo) {
    console.log("updating alerts with info ", value);
    if (!value.issues) {
      console.log("no info");
      return;
    }
    let alertData = new AlertData();
    alertData.dates = [];
    this.alertCount = 0;
    let ticketAlerts = value.issues.filter(
      (issue) => issue.tickets && issue.tickets.length > 0
    );

    ticketAlerts.forEach((ticketAlert) => {
      const filteredTickets = this.filterTicketsByTeam(ticketAlert.tickets);
      filteredTickets.forEach((value1) => {
        if (
          ticketAlert.rule !== "Follow on required" &&
          ticketAlert.rule !== "Normal Priority Reservation Exceeded" &&
          ticketAlert.rule !== "High Priority Reservation Exceeded" &&
          ticketAlert.rule !== "Emergency Priority Reservation Exceeded"
        ) {
          this.alertCount++;
          if (this.tickets.has(value1)) {
            this.updateDayProblemTickets(
              alertData,
              ticketAlert,
              this.tickets.get(value1)
            );
          } else {
            this.ticketStore
              .loadSingleProjection<Ticket_DetailProjection>(
                Ticket_DetailProjection,
                value1,
                "detail"
              )
              .then((value2) => {
                this.tickets.set(value1, value2);
                this.updateDayProblemTickets(alertData, ticketAlert, value2);
              });
          }
        }
      });
    });
    this.hasAlerts = this.alertCount > 0;
    this.alertData = alertData;
  }

  updateDayProblemTickets(
    alertData: AlertData,
    issue: SolverIssue,
    ticket: Ticket_DetailProjection
  ) {
    let date = moment(ticket.enrouteTime).startOf("day").parseZone().toDate();
    if (
      alertData.dates.filter((dayData) =>
        moment(dayData.date).isSame(moment(date), "day")
      ).length === 0
    ) {
      let dayProblem = new DayProblems();
      dayProblem.date = date;
      dayProblem.resourceAvailabilities = [];
      dayProblem.tickets = [];
      dayProblem.totalAlerts = 0;
      alertData.dates.push(dayProblem);
      // Tickets can be loaded asynchronously, so new buckets may be pushed
      // from promise callbacks after the initial pass. Keep the buckets in
      // date order on every insert rather than sorting once up front (#465).
      alertData.dates.sort((a, b) => a.date.getTime() - b.date.getTime());
    }
    let savedDayProblem = alertData.dates.filter((dayData) =>
      moment(dayData.date).isSame(moment(date), "day")
    )[0];

    if (
      savedDayProblem.tickets.filter((value) => value.ticketId === ticket.id)
        .length === 0
    ) {
      let ticketIssue = new TicketIssue();
      ticketIssue.ticket = ticket;
      ticketIssue.ticketId = ticket.id;
      ticketIssue.issues = [];
      savedDayProblem.tickets.push(ticketIssue);
    }

    let savedAvailabilityIssue = savedDayProblem.tickets.filter(
      (value) => value.ticketId === ticket.id
    )[0];
    savedAvailabilityIssue.issues.push(translateSolverRule(issue.rule));

    savedDayProblem.totalAlerts++;
  }

  ngOnInit() {
    const init = async () => {
      if (this.clientConfigService.config.restrictAlertsByPlannerTeams) {
        const planner = await this.userService.getPlanner();
        if (planner.teamList) {
          this.plannerTeamNames = new Set(planner.teamList.map((t) => t.name));
        }
      }
      this.solver.status().then((value) => {
        this.lastAlertData = value;
        this.updateCounts(value);
      });
      this.solver.updateStream.subscribe((value) => {
        this.lastAlertData = value;
        this.updateCounts(value);
      });
    };
    init();
  }

  openTicketDialog(ticket: Ticket_DetailProjection) {
    // Meetings have no job on their visit, and the ticket dialog depends on a
    // job being present, so open the meeting dialog for meeting appointments.
    if (ticket.visit?.meeting) {
      this.dialog.open(MeetingActionDialogComponent, {
        hasBackdrop: true,
        width: "550px",
        panelClass: "toolbar-popup-dialog",
        data: {
          ticketId: ticket.id
        }
      });
    } else {
      this.dialog.open(TicketActionDialogComponent, {
        hasBackdrop: true,
        width: "500px",
        panelClass: "toolbar-popup-dialog",
        data: {
          ticketId: ticket.id
        }
      });
    }
  }

  openResourceAvailabilityDialog(availabilityId: number) {
    this.dialog.open(AvailabilityEditorComponent, {
      panelClass: "toolbar-popup-dialog",
      data: {
        resourceAvailabilityId: availabilityId
      }
    });
  }

  onMenuOpen() {
    this.updateAlerts(this.lastAlertData);
  }

  private updateCounts(value: SolverInfo) {
    if (!value.issues) {
      console.log("no info");
      return;
    }
    this.alertCount = 0;
    let ticketAlerts = value.issues.filter(
      (issue) => issue.tickets && issue.tickets.length > 0
    );

    ticketAlerts.forEach((ticketAlert) => {
      const filteredTickets = this.filterTicketsByTeam(ticketAlert.tickets);
      filteredTickets.forEach(() => {
        if (
          ticketAlert.rule !== "Follow on required" &&
          ticketAlert.rule !== "Normal Priority Reservation Exceeded" &&
          ticketAlert.rule !== "High Priority Reservation Exceeded" &&
          ticketAlert.rule !== "Emergency Priority Reservation Exceeded"
        ) {
          this.alertCount++;
        }
      });
    });
    this.hasAlerts = this.alertCount > 0;
  }
}
